import { useEffect, useRef } from "react";
import { useDateTimeRangeStore } from "@/shared/stores/useDateTimeRangeStore";
import { useTimezoneStore } from "@/shared/stores/useTimezoneStore";
import { useOrgTimeZone } from "@/shared/utils/date/org-time";
import { SHIFT_DEFINITIONS, getShiftRange } from "@/shared/utils/shifts";
import {
  instantFromZonedWallClock,
  zonedDayString,
  zonedTimeString,
} from "@/shared/utils/date/zoned-wall-clock";

/**
 * Re-resolves the selected range when the active location's timezone changes.
 *
 * The rule is "06:00–12:00 means 06:00–12:00 AT THE SITE", so switching to a
 * location in another zone KEEPS THE WALL-CLOCK NUMBERS and moves the instants.
 * Switching from a São Paulo site to a Perth site leaves the picker reading
 * 06:00–12:00; it is now Perth's 06:00–12:00, and the data below it changes.
 * (The alternative — keep the instant, let the numbers change — would show the
 * operator 17:00–23:00 for a shift they think of as the morning one.)
 *
 * Two windows are deliberately exempt:
 *  - the rolling "last N" presets, which end at `now` and are the same instants
 *    in every zone; re-resolving their wall clock would drag them into the past.
 *  - a shift preset, which is re-derived from its definition in the new zone
 *    rather than copied, so it always lands on that site's current calendar day.
 *
 * Leaving a location page clears the active location, which drops the resolved
 * zone back to the viewer's. That is NOT a site switch, so it is ignored — the
 * window keeps the site basis it was last given rather than silently
 * reinterpreting itself every time the operator navigates to an org-wide page.
 */
export const useDateTimeRangeZoneSync = () => {
  const timeZone = useOrgTimeZone();
  const activeLocationId = useTimezoneStore((s) => s.activeLocationId);
  const value = useDateTimeRangeStore((s) => s.value);
  const setValue = useDateTimeRangeStore((s) => s.setValue);
  const selectedPresetKey = useDateTimeRangeStore((s) => s.selectedPresetKey);

  // Latest range/preset without making them effect dependencies — the effect
  // must run on a ZONE change only, never on every range edit (that would loop).
  const latest = useRef({ value, selectedPresetKey });
  latest.current = { value, selectedPresetKey };

  const previousZone = useRef<string | null>(null);

  useEffect(() => {
    // No location active — not a site switch. Remember nothing, change nothing.
    if (activeLocationId == null) return;

    const from = previousZone.current;
    previousZone.current = timeZone;
    // First resolution (mount, or the zone map arriving) is not a switch.
    if (from === null || from === timeZone) return;

    const { value: range, selectedPresetKey: presetKey } = latest.current;

    const shift = SHIFT_DEFINITIONS.find((s) => s.key === presetKey);
    if (shift) {
      const { from: shiftFrom, to: shiftTo } = getShiftRange(shift, new Date(), timeZone);
      setValue({ fromDate: shiftFrom, toDate: shiftTo });
      return;
    }
    // A rolling "last N" preset is zone-independent — leave it alone.
    if (presetKey && presetKey !== "custom") return;

    // Custom (or no preset): carry the wall-clock reading over to the new zone.
    const carry = (instant: Date) =>
      instantFromZonedWallClock(
        `${zonedDayString(instant, from)} ${zonedTimeString(instant, from)}:00`,
        timeZone,
      );
    setValue({ fromDate: carry(range.fromDate), toDate: carry(range.toDate) });
  }, [timeZone, activeLocationId, setValue]);
};
